import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Header, Segment, Button, Container } from "semantic-ui-react";
import { NonWeightedUserResponseDto } from "../../../models/nonWeightedUserResponseDto";
import parse from "html-react-parser";
import agent from "../../../api/agent";
import LikertControl from "../../Common/likertControl";
import YesNoControl from "../../Common/yesNoControl";
import TextQuestionControl from "../../Common/textQuestionControl";
import McqQuestionControl from "../../Common/mcqQuestionControl";
import { useStore } from "../../../stores/store";

interface Props {
  onNavigateNext: () => void;
  onNavigatePrevious: () => void;
  userSurveyId: number;
}

export default function PostSurveySelfReflection({
  onNavigateNext,
  onNavigatePrevious,
  userSurveyId,
}: Props) {
  const { t } = useTranslation("common");
  const { commonStore } = useStore();
  const [loading, setLoading] = useState<boolean>(false);
  const [pageIndex, setPageIndex] = useState<number>(1);
  const [invalidQuestions, setInvalidQuestions] = useState<number[]>([]);
  const [responses, setResponses] = useState<{ [questionNumber: number]: NonWeightedUserResponseDto }>(
    {}
  );

  const rankingQuestioOptions = [
    {
      englishText: "Professional Development",
      translatedText: t("Feedback.ProfessionalDevelopment"),
      value: 1,
    },
    {
      englishText: "Digital Resources",
      translatedText: t("Feedback.DigitalResources"),
      value: 2,
    },
    {
      englishText: "Teaching And Learning",
      translatedText: t("Feedback.TeachingAndLearning"),
      value: 3,
    },
    {
      englishText: "Assessment",
      translatedText: t("Feedback.Assessment"),
      value: 4,
    },
    {
      englishText: "Empowering Learners",
      translatedText: t("Feedback.EmpoweringLearners"),
      value: 5,
    },
    {
      englishText: "Facilitating Learners Digital Competence",
      translatedText: t("Feedback.FacilitatingLearnersDigitalCompetence"),
      value: 6,
    },
  ];

  const questionsCount: number = 16;
  const skipValidation: number[] = [13, 16];

  useEffect(() => {
    //initialize new question responses
    if (responses && responses[1] && responses[1] != null) return;
    let newQuestions: { [questionNumber: number]: NonWeightedUserResponseDto } = {};

    for (var i = 1; i <= questionsCount; i++) {
      newQuestions[i] = {
        id: 0,
        questionNumber: i,
        responseId: 0,
        hasMultipleResponses: false,
        userSurveyId: 0,
        responseValues: null,
        question: null,
        response: null,
      };
    }
    setResponses(newQuestions);
  }, []);

  const validatePage = (fromQuestion: number, toQuestion: number): boolean => {
    let invalid: number[] = [];
    for (var i = fromQuestion; i <= toQuestion; i++) {
      if (skipValidation.find((q) => q == i) != undefined) continue;

      if (
        responses[i].response == null ||
        responses[i].response == undefined ||
        responses[i].response === ""
      )
        invalid.push(i);
    }
    setInvalidQuestions(invalid);
    return invalid.length === 0;
  };

  const updateResponse = (
    questionNumber: number,
    question: string,
    response: string,
    responseId: number
  ) => {
    let updateResponses: { [questionNumber: number]: NonWeightedUserResponseDto } = {};
    updateResponses = { ...responses };
    updateResponses[questionNumber].question = question;
    updateResponses[questionNumber].response = response;
    updateResponses[questionNumber].responseId = responseId;
    updateResponses[questionNumber].userSurveyId = commonStore.currentUserSurveyId;
    setResponses(updateResponses);

    //remove question from invalid list
    let index = invalidQuestions.indexOf(questionNumber);

    if (index >= 0) {
      let newList = invalidQuestions.filter((q) => q !== questionNumber);
      setInvalidQuestions(newList);
    }
  };

  const saveQuestionaire = () => {
    setLoading(true);
    let dtos: NonWeightedUserResponseDto[] = [];
    for (var i = 1; i <= questionsCount; i++) {
      dtos.push({ ...responses[i], userSurveyId: commonStore.currentUserSurveyId });
    }
    agent.UserSurveys.savePostSurveyQuestions(dtos)
      .then((result) => onNavigateNext())
      .catch((error) => {})
      .finally(() => {
        setLoading(false);
      });
  };

  const pageOne = (
    <>
      {/* <div className="pre-post-txt">{parse(t("Feedback.SubHeader1"))}</div>*/}
      <div className="pre-post-txt">{parse(t("Feedback.SubHeader2"))}</div>

      {/* 1.	The layout and navigation through the different sections of the tool (intro pages, competence areas and questions) areas is user friendly. */}
      <LikertControl
        invalid={invalidQuestions.find((c) => c === 1) != undefined}
        value={responses[1] ? responses[1].responseId : null}
        questionHeading={t("Feedback.Q1")}
        onValueChange={(value: number, text: string) => {
          updateResponse(
            1,
            "1.	The layout and navigation through the different sections of the tool (intro pages, competence areas and questions) areas is user friendly.",
            text,
            value
          );
        }}
      />

      {/* 2.	The introduction/ instructions for using the tool are helpful. */}
      <LikertControl
        invalid={invalidQuestions.find((c) => c === 2) != undefined}
        value={responses[2] ? responses[2].responseId : null}
        questionHeading={t("Feedback.Q2")}
        onValueChange={(value: number, text: string) => {
          updateResponse(
            2,
            "2.	The introduction/ instructions for using the tool are helpful.",
            text,
            value
          );
        }}
      />

      {/* 3.	The glossary is helpful to understand the self-reflection questions.  */}
      <LikertControl
        invalid={invalidQuestions.find((c) => c === 3) != undefined}
        value={responses[3] ? responses[3].responseId : null}
        questionHeading={t("Feedback.Q3")}
        onValueChange={(value: number, text: string) => {
          updateResponse(
            3,
            "3.	The glossary is helpful to understand the self-reflection questions.",
            text,
            value
          );
        }}
      />

      {/* 4.	In the future, I would like to save my session and log in to the tool again at different time(s) */}
      <McqQuestionControl
        invalid={invalidQuestions.find((c) => c === 4) != undefined}
        questionHeading={t("Feedback.Q4")}
        value={responses[4] ? responses[4].responseId : null}
        choices={[
          {
            englishText: "No",
            translatedText: t("Common.No"),
            value: 1,
          },
          { englishText: "Yes", translatedText: t("Common.Yes"), value: 2 },
        ]}
        onValueChange={(value: number, text: string) => {
          updateResponse(
            4,
            "4.	In the future, I would like to save my session and log in to the tool again at different time(s)",
            text,
            value
          );
        }}
      />

      {/* 5.	Did you encounter any issues when using the tool?  */}
      <YesNoControl
        invalid={invalidQuestions.find((c) => c === 5) != undefined}
        value={responses[5] ? responses[5].responseId : null}
        valueText={responses[5] ? responses[5].response : null}
        questionHeading={t("Feedback.Q5")}
        onValueChange={(value: number, text: string) => {
          updateResponse(5, "5.	Did you encounter any issues when using the tool?", text, value);
        }}
      />
    </>
  );

  const pageTwo = (
    <>
      <div className="pre-post-txt">{parse(t("Feedback.Section2Header"))}</div>

      {/* 6.	The questions relate to my everyday practice. */}
      <LikertControl
        invalid={invalidQuestions.find((c) => c === 6) != undefined}
        value={responses[6] ? responses[6].responseId : null}
        questionHeading={t("Feedback.Q6")}
        onValueChange={(value: number, text: string) => {
          updateResponse(6, "6.	The questions relate to my everyday practice.", text, value);
        }}
      />

      {/* 7.	The questions are relevant to my professional learning and development */}
      <LikertControl
        invalid={invalidQuestions.find((c) => c === 7) != undefined}
        value={responses[7] ? responses[7].responseId : null}
        questionHeading={t("Feedback.Q7")}
        onValueChange={(value: number, text: string) => {
          updateResponse(
            7,
            "7.	The questions are relevant to my professional learning and development",
            text,
            value
          );
        }}
      />

      {/* 8.	The questions are easy to understand */}
      <LikertControl
        invalid={invalidQuestions.find((c) => c === 8) != undefined}
        value={responses[8] ? responses[8].responseId : null}
        questionHeading={t("Feedback.Q8")}
        onValueChange={(value: number, text: string) => {
          updateResponse(8, "8.	The questions are easy to understand", text, value);
        }}
      />

      {/* 9.	The progression of the proficiency levels within the questions is distinctive. */}
      <LikertControl
        invalid={invalidQuestions.find((c) => c === 9) != undefined}
        value={responses[9] ? responses[9].responseId : null}
        questionHeading={t("Feedback.Q9")}
        onValueChange={(value: number, text: string) => {
          updateResponse(
            9,
            "9.	The progression of the proficiency levels within the questions is distinctive.",
            text,
            value
          );
        }}
      />

      {/* 10.	The questions help me to reflect about my digital competence. */}
      <LikertControl
        invalid={invalidQuestions.find((c) => c === 10) != undefined}
        value={responses[10] ? responses[10].responseId : null}
        questionHeading={t("Feedback.Q10")}
        onValueChange={(value: number, text: string) => {
          updateResponse(
            10,
            "10.	The questions help me to reflect about my digital competence.",
            text,
            value
          );
        }}
      />

      {/* 11.	The length of the self-reflection tool is appropriate. */}
      <LikertControl
        invalid={invalidQuestions.find((c) => c === 11) != undefined}
        value={responses[11] ? responses[11].responseId : null}
        questionHeading={t("Feedback.Q11")}
        onValueChange={(value: number, text: string) => {
          updateResponse(11, "11.	The length of the self-reflection tool is appropriate.", text, value);
        }}
      />

      {/* 12.	The grammar, syntax, and spelling of the questions is correct. */}
      <LikertControl
        invalid={invalidQuestions.find((c) => c === 12) != undefined}
        value={responses[12] ? responses[12].responseId : null}
        questionHeading={t("Feedback.Q12")}
        onValueChange={(value: number, text: string) => {
          updateResponse(
            12,
            "12.	The grammar, syntax, and spelling of the questions is correct.",
            text,
            value
          );
        }}
      />

      {/* 13.	Please provide comments on the question set, if any?  */}
      <TextQuestionControl
        invalid={false}
        value={responses[13] ? responses[13].response : null}
        questionHeading={t("Feedback.Q13")}
        onValueChange={(value: number, text: string) => {
          updateResponse(13, "13.	Please provide comments on the question set, if any?", text, value);
        }}
      />

      {/* 14.	How much time in total did it take you to answer the 32 self- reflection questions? */}
      <McqQuestionControl
        invalid={invalidQuestions.find((c) => c === 14) != undefined}
        value={responses[14] ? responses[14].responseId : null}
        questionHeading={t("Feedback.Q14")}
        choices={[
          {
            englishText: "Less than 15 min",
            translatedText: t("Feedback.LessThan15Minutes"),
            value: 1,
          },
          { englishText: "15-30 min", translatedText: t("Feedback.15To30Minutes"), value: 2 },
          { englishText: "30-45 min", translatedText: t("Feedback.30To45Minutes"), value: 3 },
          {
            englishText: "More than 45 min",
            translatedText: t("Feedback.MoreThan45Minutes"),
            value: 4,
          },
        ]}
        onValueChange={(value: number, text: string) => {
          updateResponse(
            14,
            "14.	How much time in total did it take you to answer the 32 self- reflection questions?",
            text,
            value
          );
        }}
      />

      {/* 15.	What competence area to you consider to be the most important?  */}
      <McqQuestionControl
        questionHeading={t("Feedback.Q15")}
        invalid={invalidQuestions.find((c) => c === 15) != undefined}
        value={responses[15] ? responses[15].responseId : null}
        onValueChange={(value: number, text: string) => {
          updateResponse(
            15,
            "15.	What competence area to you consider to be the most important? ",
            text,
            value
          );
        }}
        choices={rankingQuestioOptions}
      />

      {/* 16.	What suggestions or recommendations do you have to improve the RefleCT tool for teachers?  */}
      <TextQuestionControl
        invalid={false}
        value={responses[16] ? responses[16].response : null}
        questionHeading={t("Feedback.Q16")}
        onValueChange={(value: number, text: string) => {
          updateResponse(
            16,
            "16.	What suggestions or recommendations do you have to improve the RefleCT tool for teachers?",
            text,
            value
          );
        }}
      />

      {/* Thank you for taking the time to share your feedback on the RefleCT tool. Your insights are invaluable to us as we strive to improve and better serve teachers.  */}
      <p className="thx">{parse(t("Feedback.FeedbackFooter"))}</p>
      {/* Please continue to download your feedback report.  */}
      <p className="thx">{parse(t("Feedback.FeedbackFooterSub"))}</p>
    </>
  );

  return (
    <>
      <Segment inverted color="blue" className="survey-intro-header">
        <Header as="h1"> {t("Feedback.Header")}</Header>
      </Segment>

      <div className="likert-wrap">
        <form action="">
          {pageIndex === 1 && pageOne}

          {pageIndex === 2 && pageTwo}
        </form>
      </div>

      <div className="pre-post-txt"></div>
      <Container className="survey-btns">
        {invalidQuestions.length > 0 && (
          <div id="divError">{t("Feedback.MissingRequiredFieldsErrorMessage")}</div>
        )}
        <Segment>
          <Button
            primary
            disabled={loading}
            floated="left"
            onClick={() => {
              if (pageIndex === 2) setPageIndex(1);
              else onNavigatePrevious();
            }}
          >
            {t("Common.Previous")}
          </Button>

          <Button
            primary
            floated="right"
            loading={loading}
            onClick={() => {
              if (pageIndex === 1 && validatePage(1, 5)) {
                setPageIndex(2);
              } else if (pageIndex === 2 && validatePage(6, questionsCount)) {
                saveQuestionaire();
              } else return;
            }}
          >
            {t("Common.Next")}
          </Button>
        </Segment>
      </Container>
    </>
  );
}
